import BackendQuery from 'decentralized/BackendQuery.js';
import SoundUploader from 'api/SoundUploader.js';
import {ipfsHashToBytes32} from 'utils/ipfsHash.js';
import {
    SampleCreated,
    SampleStyle,
    SampleYoutube,
    SampleTagged,
    SampleCountry,
    SampleYear,
    NewDiscogsSample,
} from 'decentralized/EthereumEvents.js';
import EthereumTransaction from 'decentralized/transactions/EthereumTransaction.js';
import {GUILD_SAMPLES} from '../transactions/contracts.js';
import GuildSamplesContract from 'decentralized/contracts/GuildSamplesContract.js';
const contract = new GuildSamplesContract();

class GuildSamples {
    constructor(web3) {
        this.web3 = web3;
        this.contract = new web3.eth.Contract(contract.abi, contract.contractAddress);
    }

    get address() {
        return this.contract.options.address;
    }

    async getGuildIdForSound(soundHash) {
        let guildIds = await new BackendQuery(this.address)
            .from(SampleCreated)
            .select('guildId')
            .where('ipfsHash', soundHash)
            .executeQuery();

        let x = guildIds.map(t => t.guildId)[0];
        if (x === null || x === undefined) {
            return 0;
        }

        return x;
    }

    async getMetadataForSound(soundHash, eventType, fields) {
        let query = await new BackendQuery(this.address)
            .from(eventType)
            .where('ipfsHash', soundHash);

        fields.forEach(field => query.select(field));
        return await query.executeQuery();
    }

    async getTagsForSound(soundHash) {
        let tags = await this.getMetadataForSound(
            soundHash,
            SampleTagged,
            ['tag']);
        return Array.from(new Set(tags.map(t => t.tag)));
    }

    async getTitleForSound(soundHash) {
        let titles = await this.getMetadataForSound(
            soundHash,
            SampleCreated,
            ['title']);

        if (titles.length === 0) {
            return undefined;
        }
        return titles[0].title;
    }

    async getStylesForSound(soundHash) {
        let styles = await this.getMetadataForSound(
            soundHash,
            SampleStyle,
            ['style']);
        return Array.from(new Set(styles.map(t => t.style)));
    }

    async getCountryForSound(soundHash) {
        let countries = await this.getMetadataForSound(
            soundHash,
            SampleCountry,
            ['country']);
        if (countries.length === 0) {
            return undefined;
        }
        return countries[0].country;
    }

    async getYearForSound(soundHash) {
        let years = await this.getMetadataForSound(
            soundHash,
            SampleYear,
            ['year']);
        if (years.length === 0) {
            return undefined;
        }
        return years[0].year;
    }

    async getYoutubeIdForSound(soundHash) {
        let data = await this.getMetadataForSound(
            soundHash,
            SampleYoutube,
            ['videoId']);

        if (data.length === 0) {
            return undefined;
        }
        return data[0].videoId;
    }

    async getDiscogsData(soundHash) {
        let discogsData = await this.getMetadataForSound(
            soundHash,
            NewDiscogsSample,
            ['covertArtHash, discogsId']);
        return discogsData[0] || {coverArtHash: undefined, discogsId: undefined};
    }

    async saveSample(sample) {
        let id = sample.getId();
        let soundUploader = new SoundUploader(sample.audioBuffer.numberOfChannels, () => []);
        let newIpfsHash = await soundUploader.uploadBufferToIpfs(sample.audioBuffer);
        let tags = await this.getTagsForSound(id);
        let title = await this.getTitleForSound(id);
        let styles = await this.getStylesForSound(id);
        let country = await this.getCountryForSound(id);
        let year = await this.getYearForSound(id);
        let {coverArtHash, discogsId} = await this.getDiscogsData(id);
        let youtubeId = await this.getYoutubeIdForSound(id);

        this.newSampleWithMetadata(
            newIpfsHash,
            {
                title,
                youtubeId,
                tags,
                styles,
                country,
                year,
                guildId: 0,
                discogsId,
                coverArtHash
            });

        return newIpfsHash;
    }

    async newSampleWithMetadata(ipfsHash, obj) {
        return new Promise(async (resolve, reject) => {
            let {title, youtubeId, tags, styles, country, year, guildId, discogsId, coverArtHash} = obj;
            let web3 = this.web3;
            let nullBytes32 = web3.utils.padLeft('0x0', 64);
            if (tags === undefined) {
                tags = [];
            }
            if (tags.length > 4) {
                tags = tags.slice(0, 4);
            }
            if (styles.length > 4) {
                styles = styles.slice(0, 4);
            }
            for (let i=tags.length; i < 4; i++) {
                tags.push(null);
            }
            for (let i=styles.length; i < 4; i++) {
                styles.push(null);
            }

            tags = tags.map(x => !x ? nullBytes32 : web3.utils.fromAscii(x));
            styles = styles.map(x => !x ? nullBytes32 : web3.utils.fromAscii(x));
            country = !country ? nullBytes32 : web3.utils.fromAscii(country);
            youtubeId = !youtubeId ? nullBytes32 : web3.utils.fromAscii(youtubeId);
            coverArtHash = !coverArtHash ? nullBytes32 : ipfsHashToBytes32(coverArtHash);

            ipfsHash = ipfsHashToBytes32(ipfsHash);

            let args = [
                    ipfsHash,
                    title,
                    youtubeId,
                    tags,
                    styles,
                    country,
                    year === undefined ? 0 : year,
                    discogsId === undefined ? 0 : discogsId,
                    coverArtHash,
                    guildId];

            new EthereumTransaction(
                GUILD_SAMPLES,
                'newSampleWithMetadata',
                args)
                .execute()
                .then(() => resolve());
        });
    }
}

export default GuildSamples;
